import React from "react";
import moment from "moment-timezone";
import PropTypes from "prop-types";

import { ErrorState } from "candidate/ErrorState";
import { CardLevelOne } from "common/DS/ElevatedCard";
import { TextButton } from "common/DS/Buttons";
import List from "common/DS/List";
import ListItem from "common/DS/ListItem";
import { H4, H6 } from "common/DS/Text";
import { useIsBreakpoint } from "lib/hooks/window";
import { TranslationFn } from "lib/types";

export const NoSurveysCard = (
    { companyName = "" }: { companyName?: string },
    { t: _ }: { t: TranslationFn },
) => {
    const errorText = _(
        "It looks like there are currently no surveys available for you to take. \n \n    Your voice is important to us. Keep an eye out for new opportunities \n \n    to share your feedback.",
    );
    const errorTitle = _("No Surveys Available");
    return (
        <div className="hub__surveys">
            <ErrorState
                errorTitle={errorTitle}
                errorText={errorText}
                companyName={companyName}
                showLanguageSelector={false}
                className="hub__surveys-content"
            />
        </div>
    );
};

NoSurveysCard.contextTypes = {
    t: PropTypes.func,
};

type SurveyListItem = {
    displayName: string;
    redirectURL: string;
    daysLeft: number;
    completedDate: string;
    closeDate: string;
};

type SurveyHelperTextProps = {
    daysLeft: number;
    completedDate: string;
    closeDate: string;
};

type HubSurveysCardProps = {
    surveyList: Array<SurveyListItem>;
    firstName: string;
};

const SurveyDisplayStatus = {
    CLOSED: 0,
    COMPLETED: 1,
    OPEN: 2,
};

function getSurveyDisplayStatus(completedDate: string, closeDate: string) {
    if (completedDate) {
        return SurveyDisplayStatus.COMPLETED;
    }
    if (closeDate && moment.utc(closeDate).isBefore(moment.utc()))
        return SurveyDisplayStatus.CLOSED;

    return SurveyDisplayStatus.OPEN;
}

const SurveyHelperText = (
    { completedDate, closeDate, daysLeft }: SurveyHelperTextProps,
    { t: _ }: { t: TranslationFn },
) => {
    const status = getSurveyDisplayStatus(completedDate, closeDate);
    let daysLeftText = "";
    // Always On surveys don't have an end date, so we don't show days left
    if (daysLeft !== null) {
        if (daysLeft > 0) {
            daysLeftText = _("{daysLeft} Days Left", { daysLeft });
        } else {
            daysLeftText = _("Ends Today");
        }
    }
    return (
        <>
            {status === SurveyDisplayStatus.COMPLETED && (
                <span className="color-navy-medium">
                    {_(`Completed {completedDate}`, { completedDate })}
                </span>
            )}
            {status === SurveyDisplayStatus.CLOSED && (
                <span className="color-navy-medium">{_(`Closed {closeDate}`, { closeDate })}</span>
            )}
            {status === SurveyDisplayStatus.OPEN && (
                <span className="color-indigo">{daysLeftText}</span>
            )}
        </>
    );
};

SurveyHelperText.contextTypes = {
    t: PropTypes.func,
};

export const HubSurveysCard = (
    { surveyList, firstName }: HubSurveysCardProps,
    { t: _ }: { t: TranslationFn },
) => {
    const cardTitle = _(`Available surveys for {firstName}`, { firstName });
    const { breakpointXS, breakpointSM } = useIsBreakpoint();
    const showSmallView = breakpointXS || breakpointSM;
    const buttonText = _("START");

    return (
        <div className="hub__surveys">
            <CardLevelOne className="hub__surveys-content">
                {!showSmallView && <H4>{cardTitle}</H4>}
                {showSmallView && <H6>{cardTitle}</H6>}
                <List>
                    {surveyList.map((a) => (
                        <ListItem
                            className="hub__surveys-list-item"
                            content={a.displayName}
                            key={a.redirectURL}
                            secondary={<SurveyHelperText {...a} />}
                        >
                            {getSurveyDisplayStatus(a.completedDate, a.closeDate) ===
                                SurveyDisplayStatus.OPEN && (
                                <TextButton href={a.redirectURL}>{buttonText}</TextButton>
                            )}
                        </ListItem>
                    ))}
                </List>
            </CardLevelOne>
        </div>
    );
};

HubSurveysCard.contextTypes = {
    t: PropTypes.func,
};
